import { InformationCircleIcon, SearchIcon, XIcon } from "@heroicons/react/outline";
import { observer } from "mobx-react-lite";
import { useCallback, useMemo, useState } from "react";
import { Virtuoso } from "react-virtuoso";

import { useIsReadonly } from "../readonly-context";
import { useSourcesV2 } from "../SourcesField/sourcesV2Context";

import AutofillButton from "./AutofillButton";
import { getAutofillableCount, hasAutofillSuggestions } from "./autofillUtils";
import InputVariableRow from "./InputVariableRow";
import { InputVariablesFieldProps } from "./types";

import { useBlock } from "@/components/PowerBlock/Block/block-store";
import { FieldType } from "@/components/PowerBlock/Block/types/fieldTypes";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { ToastType } from "@/enums";
import { displayToast } from "@/utils/toast";

const InputVariablesField = observer(
  ({
    variables,
    sources,
    sourceKeyPrefix,
    isLoading = false,
    showAutofill = true,
    onAutofill,
    fieldType = FieldType.InputVariable,
    templateFormat,
    forceOpenVariableSelector,
  }: InputVariablesFieldProps) => {
    const readonly = useIsReadonly();
    const blockStore = useBlock();
    const sourcesV2 = useSourcesV2();
    const [highlightedVariables, setHighlightedVariables] = useState<Set<string>>(new Set());
    const [searchQuery, setSearchQuery] = useState("");

    // Extract current mappings reactively from blockStore
    const currentMappings = (() => {
      if (!blockStore.configuration) {
        return {};
      }

      const configPath = sourceKeyPrefix.split(".");
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      let currentConfig = blockStore.configuration as any;

      for (const path of configPath) {
        currentConfig = currentConfig?.[path];
      }

      // Create a new object to ensure reactivity
      const mappings: Record<string, string> = {};
      if (currentConfig && typeof currentConfig === "object") {
        for (const variable of variables) {
          if (currentConfig[variable]) {
            mappings[variable] = currentConfig[variable];
          }
        }
      }

      return mappings;
    })();

    const filteredVariables = useMemo(() => {
      if (!searchQuery.trim()) {
        return variables;
      }

      const query = searchQuery.toLowerCase().trim();
      return variables.filter(variable => {
        const cleanVariable = variable.replace(/[{}]/g, "");
        return cleanVariable.toLowerCase().includes(query);
      });
    }, [variables, searchQuery]);

    // Calculate unmapped variables without memoization for better reactivity
    const unmappedVariables = filteredVariables.filter(variable => !currentMappings[variable]);

    // Check for available suggestions without memoization for live updates
    const hasAvailableSuggestions = (() => {
      if (unmappedVariables.length === 0) {
        return false;
      }
      return hasAutofillSuggestions(unmappedVariables, sources, sourcesV2);
    })();

    const handleAutofill = useCallback(
      (mappings: Record<string, string>) => {
        if (!onAutofill) {
          return;
        }

        const mappedCount = Object.keys(mappings).length;

        if (mappedCount > 0) {
          displayToast(
            `Successfully auto-mapped ${mappedCount} variable${mappedCount === 1 ? "" : "s"}`,
            ToastType.success,
          );
        } else {
          displayToast("No suitable matches found for auto-mapping", ToastType.warning);
        }

        const mappedVariables = new Set(Object.keys(mappings));
        setHighlightedVariables(mappedVariables);

        setTimeout(() => {
          setHighlightedVariables(new Set());
        }, 2000);

        onAutofill(mappings);
      },
      [onAutofill],
    );

    const shouldShowAutofill = useMemo(
      () => showAutofill && variables.length > 0 && !isLoading && !readonly,
      [showAutofill, variables.length, isLoading, readonly],
    );

    // Calculate mappable count without memoization for real-time updates
    const mappableCount = getAutofillableCount(unmappedVariables, sources, sourcesV2);

    const shouldShowSearch = variables.length > 10;
    const shouldVirtualize = filteredVariables.length > 50;

    const normalizedForceOpenVariable = forceOpenVariableSelector?.replace(/[{}]/g, "").trim();

    const renderItem = useCallback(
      (index: number) => {
        const variable = filteredVariables[index];
        return (
          <div className="w-full py-2">
            <InputVariableRow
              key={variable}
              variableName={variable}
              sources={sources}
              sourceKeyPrefix={sourceKeyPrefix}
              fieldType={fieldType}
              isHighlighted={highlightedVariables.has(variable)}
              templateFormat={templateFormat}
              forceSelectorOpen={
                normalizedForceOpenVariable !== undefined &&
                variable.replace(/[{}]/g, "").trim() === normalizedForceOpenVariable
              }
            />
          </div>
        );
      },
      [
        filteredVariables,
        sources,
        sourceKeyPrefix,
        fieldType,
        highlightedVariables,
        templateFormat,
        normalizedForceOpenVariable,
      ],
    );

    if (isLoading) {
      return (
        <div className="space-y-3">
          {Array.from({ length: Math.max(2, variables.length || 2) }).map((_, index) => (
            <div
              // eslint-disable-next-line react/no-array-index-key
              key={index}
              className="space-y-2"
            >
              <Skeleton width={100} height={16} />
              <Skeleton height={40} className="w-full" />
            </div>
          ))}
        </div>
      );
    }

    if (variables.length === 0) {
      return null;
    }

    return (
      <div className="space-y-4">
        {shouldShowAutofill && (
          <div className="space-y-2">
            <div className="flex items-center justify-between rounded-lg bg-gray-50 px-3 py-2">
              <div className="flex items-center gap-2">
                <span className="text-sm font-medium text-gray-700">
                  {variables.length} variable{variables.length !== 1 ? "s" : ""}
                </span>
                {variables.length > 0 && (
                  <span className="text-xs text-gray-500">
                    ({Object.keys(currentMappings).length}/{variables.length} mapped)
                    {searchQuery && ` • ${filteredVariables.length} shown`}
                  </span>
                )}
              </div>
              <AutofillButton
                variables={unmappedVariables}
                sources={sources}
                sourcesV2={sourcesV2}
                onAutofill={handleAutofill}
                isLoading={isLoading}
                disabled={!hasAvailableSuggestions || readonly}
                mappableCount={mappableCount}
                readonly={readonly}
              />
            </div>
            {hasAvailableSuggestions && (
              <div className="flex items-start gap-2 rounded-md bg-blue-50 px-3 py-2 text-xs text-blue-700">
                <InformationCircleIcon className="mt-0.5 h-4 w-4 flex-shrink-0" />
                <div>
                  <span className="font-medium">Autofill mappings:</span>{" "}
                  <span className="text-blue-600">
                    Automatically maps variables to sources with identical names.
                  </span>
                </div>
              </div>
            )}
          </div>
        )}

        {shouldShowSearch && (
          <div className="relative">
            <SearchIcon className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <Input
              type="text"
              placeholder={`Search ${variables.length} variables...`}
              value={searchQuery}
              onChange={e => setSearchQuery(e.target.value)}
              className="pl-10 pr-10"
            />
            {searchQuery && (
              <Button
                variant="ghost"
                size="sm"
                onClick={() => setSearchQuery("")}
                className="absolute right-1 top-1/2 h-7 w-7 -translate-y-1/2 p-0"
              >
                <XIcon className="h-4 w-4" />
              </Button>
            )}
          </div>
        )}

        {filteredVariables.length === 0 && searchQuery ? (
          <div className="rounded-lg border border-gray-200 bg-gray-50 p-8 text-center">
            <SearchIcon className="mx-auto h-12 w-12 text-gray-400" />
            <p className="mt-2 text-sm text-gray-600">
              No variables found matching "{searchQuery}"
            </p>
            <Button variant="link" size="sm" onClick={() => setSearchQuery("")} className="mt-2">
              Clear search
            </Button>
          </div>
        ) : (
          <div className="space-y-2">
            {shouldVirtualize ? (
              <div style={{ height: "500px" }}>
                <Virtuoso
                  totalCount={filteredVariables.length}
                  itemContent={renderItem}
                  overscan={5}
                />
              </div>
            ) : (
              filteredVariables.map(variable => (
                <InputVariableRow
                  key={variable}
                  variableName={variable}
                  sources={sources}
                  sourceKeyPrefix={sourceKeyPrefix}
                  fieldType={fieldType}
                  isHighlighted={highlightedVariables.has(variable)}
                  templateFormat={templateFormat}
                  forceSelectorOpen={
                    normalizedForceOpenVariable !== undefined &&
                    variable.replace(/[{}]/g, "").trim() === normalizedForceOpenVariable
                  }
                />
              ))
            )}
          </div>
        )}
      </div>
    );
  },
);

export default InputVariablesField;
